import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

// Components
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import OrderCard from '../OrderCard';
import AddressForm from '../AddressForm';
import SavedAddresses from '../SavedAddresses';
import Checkbox from '@mui/material/Checkbox';
import Skeleton from '@mui/material/Skeleton';
import Alert from '../Alert';
import AlertConfirm from '../Consultancy/AlertConfirm';

// Queries
import getAgregarAlCarrito from '../Queries/getAgregarAlCarrito';
import getGlobal from '../Queries/getGlobal';
import postGlobal from '../Queries/postGlobal';

import styles from './index.module.sass';

export default function AddressSelect({
  device,
  history,
  webservice,
  getCookie,
  match,
  setSessionMessage,
}) {
  const { t } = useTranslation(['order', 'address']);

  const [initialState, setInitialState] = useState(false);
  const [alertOpen, setalertOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [alertText, setAlertText] = useState('');
  const [orderData, setOrderData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingAddress, setLoaddingAddress] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);
  const [openAddress, setOpenAddress] = useState(false);
  const [addressSelected, setAddressSelected] = useState({ type: 'kit', pos: null, val: {} });
  const [addressForm, setAddressForm] = useState(false);
  const [shippingAddressChecked, setShippingAddressChecked] = useState(false);
  const [extraShippinghargeTitle, setExtraShippinghargeTitle] = useState('');
  const [extraShippingharge, setExtraShippingharge] = useState(
    orderData?.domicilioEnvio?.confirmacionEnvio ||
      orderData?.domicilioEnvio?.bloquearCambioDomicilio === 'true' ||
      false,
  );
  const [paqueterias, setPaqueterias] = useState([]);
  const [paqueteria, setPaqueteria] = useState('');

  const { cliente = {}, domicilioEnvio = {} } = orderData;
  const { cp } = cliente;

  const { screenSize } = device;

  function hadleBack() {
    history.push('/cart');
  }

  const getOrder = useCallback(
    async (action, cat, id, quantity, lineNum, neddLoading) => {
      setLoading(neddLoading);
      setFinishLoading(true);
      const order = await getAgregarAlCarrito({
        jsonBody: {
          accion: action,
          usuarioOK: getCookie('usuarioOk'),
          folioPedido: getCookie('folioPedido'),
          almacen: getCookie('warehouse'),
          categoria: cat,
          clave: id,
          cantidad: quantity,
          numeroLinea: lineNum,
          textoProdSeleccion: '',
          idioma: window?.localStorage?.getItem('i18nextLng')?.includes('es') ? 0 : 1,
          origen: 'CARRITONUE',
        },
      });
      const { tipoMensaje, mensaje } = order;
      if (mensaje !== '' && tipoMensaje === 'error') {
        setAlertText(mensaje);
        setalertOpen(true);
      }

      setOrderData(order || {});

      setExtraShippingharge(
        order?.domicilioEnvio?.confirmacionEnvio ||
          orderData?.domicilioEnvio?.bloquearCambioDomicilio === 'true' ||
          false,
      );

      const getPaqueterias = await getGlobal({
        tmbservice: 'getPaqueterias',
        jsonBody: {
          estado: order?.domicilioEnvio?.colonia || '',
        },
      });

      const {
        typeMessage: typeMessagePaq = '',
        message: messagePaq = '',
        dataDetallePaqueteria = [],
      } = getPaqueterias;

      if (messagePaq !== '' && typeMessagePaq === 'error') {
        setAlertText(messagePaq);
        setalertOpen(true);
      }

      setPaqueterias(dataDetallePaqueteria);
      setPaqueteria(order?.domicilioEnvio?.paqueteria || '');

      setLoading(false);
      setFinishLoading(false);

      setSessionMessage(order?.mensaje || '');
    },
    [getCookie, orderData?.domicilioEnvio?.bloquearCambioDomicilio, setSessionMessage],
  );

  const updateParcel = useCallback(
    async (parcel) => {
      if (!parcel) return;

      const response = await postGlobal({
        tmbservice: 'postActualizarDomicilio',
        jsonBody: {
          usuarioOK: getCookie('usuarioOk'),
          folioPedido: getCookie('folioPedido'),
          soloPaqueteria: 1,
          paqueteria: parcel,
          cp: cp,
        },
      });

      const { tipoMensaje = 'error', mensaje = '' } = response;
      setSessionMessage(mensaje);
      if (tipoMensaje === 'error') {
        setAlertText(mensaje);
        setalertOpen(true);
        if (mensaje === '498' || mensaje === '499') return false;
        setLoading(false);
        return false;
      } else {
        getOrder('CERRAR01WEB', '', '', '', '', true, '');
      }
    },
    [getCookie, setSessionMessage, getOrder, cp],
  );

  const changeValue = (e) => {
    setPaqueteria(e.target.value);
    updateParcel(e.target.value);
  };

  const postConfirmacionEnvio = useCallback(
    async (extraShippCharge) => {
      setFinishLoading(true);

      setExtraShippingharge(extraShippCharge);

      const confirm = await postGlobal({
        tmbservice: 'postConfirmacionEnvio',
        jsonBody: {
          noPedido: getCookie('folioPedido'),
          confirmacionEnvio: extraShippCharge,
          idioma: window?.localStorage?.getItem('i18nextLng')?.includes('es') ? 0 : 1,
        },
      });

      const { typeMessage = 'error' } = confirm;

      if (typeMessage !== 'success') {
        setExtraShippingharge(!extraShippCharge);
      } else {
        await getOrder('CERRAR01WEB', '', '', '', '', true);
      }
      setFinishLoading(false);
    },
    [getCookie, getOrder],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!initialState) {
      setInitialState(true);
      getOrder('CERRAR01WEB', '', '', '', '', true);
    }
  }, [getOrder, t, initialState]);

  async function handleClick() {
    if (!shippingAddressChecked) {
      setAlertText(t('address:useThisShippingAddressAlert'));
      setalertOpen(true);
      return;
    }

    if (!paqueteria) {
      setAlertText(t('order:parcelAlert'));
      setalertOpen(true);
      return;
    }

    if (!extraShippingharge && orderData?.domicilioEnvio?.paqueteria !== 'USPS') {
      setExtraShippinghargeTitle(t('order:shippingChargeAlertTitle'));
      setConfirmText(t('order:shippingChargeAlert'));
      setConfirmOpen(true);
      return;
    }

    setFinishLoading(true);

    history.push('/order');

    //   const irelandPayToken = await GetTokenIrelandPay({
    //     jsonBody: { idioma: 1, amount: importeAPagar, orderNumber: folioPedido },
    //   });

    //   const { token = '', message = '', typeMessage = 'error' } = irelandPayToken;

    //   setSessionMessage(message);

    //   if (token && typeMessage === 'success') {
    //     history.push(`${match.path}/payment/${token}`);
    //   } else {
    //     setFinishLoading(false);
    //     setOpenMessage(true);
    //     window.scrollTo(0, 0);
    //   }
  }

  const confirmCallback = useCallback(() => {
    setConfirmOpen(false);
    setFinishLoading(true);
    history.push('/order');
  }, [history]);

  const ContinueButton = () => {
    return (
      <Button
        type='submit'
        variant='contained'
        size='large'
        color='primary'
        className={(orderData.situacionCierre !== 'LISTO' && styles.disabled) || styles.button}
        onClick={() => {
          if (!finishLoading) handleClick();
        }}
        disabled={orderData.situacionCierre !== 'LISTO'}>
        {t('continue')}
        {(finishLoading && <CircularProgress className={styles.finishLoading} />) || (
          <i className={`material-icons ${styles.buttonIcon}`}>trending_flat</i>
        )}
      </Button>
    );
  };

  return (
    <div className={styles.mainContainer}>
      <AlertConfirm
        openConfirm={confirmOpen}
        setOpenConfirm={setConfirmOpen}
        title={extraShippinghargeTitle}
        text={confirmText}
        callback={confirmCallback}
        setLoading={setFinishLoading}
      />
      <Alert open={alertOpen} setOpen={setalertOpen} title={'Terramar'} text={alertText} />

      <div className={styles.orderHeader}>
        <div className={styles.return} onClick={hadleBack}>
          <i className='material-icons-outlined'>navigate_before</i>
          <span className={styles.returnText}>{t('return')}</span>
        </div>
      </div>
      <div className={styles.container}>
        <div>
          <div className={`${styles.buttonContainer} ${styles.withPaddingBottom}`}>
            <ContinueButton />
          </div>
          <div className={styles.sectionTitle}>
            <div className={styles.titleContainer}>
              <i className={`material-icons-outlined ${styles.sectionicon}`}>local_shipping</i>
              <div className={styles.sectiontext}>{t('shippingAddress')}:</div>
            </div>

            <div className={styles.checkContainer}>
              <Checkbox
                checked={shippingAddressChecked}
                style={{ padding: 0, paddingRight: 8 }}
                onChange={(event) => setShippingAddressChecked(event.target.checked)}
              />
              <span className={styles.colorBlue}>{t('address:useThisShippingAddress')}</span>
            </div>
          </div>
          <OrderCard
            open={openAddress}
            callback={setOpenAddress}
            icon='edit'
            iconColor='gold'
            iconText={t('changeAddress')}
            iconSize={18}
            title={
              <>
                <div className={styles.shippingAddress}>
                  <div className={styles.orderDelivery}>
                    <div className={styles.cardText}>
                      {((loading || loadingAddress) && (
                        <Skeleton
                          variant='rectangular'
                          animation='wave'
                          className={styles.loadingText}
                        />
                      )) ||
                        (domicilioEnvio.domicilioClienteFinalSN === 'S' &&
                          domicilioEnvio.domicilioClienteFinalNombre) ||
                        cliente?.nombreCompleto ||
                        ''}
                    </div>
                    <div className={styles.num}>
                      {((loading || loadingAddress) && (
                        <Skeleton
                          variant='rectangular'
                          animation='wave'
                          className={styles.loadingText}
                        />
                      )) || (
                        <>
                          <div>
                            {domicilioEnvio?.calle || ''} {domicilioEnvio?.entreCalle || ''}{' '}
                            {domicilioEnvio?.numeroExterior || ''}
                          </div>
                          <div>
                            {domicilioEnvio?.municipio || ''}
                            {', '}
                            {domicilioEnvio?.colonia || ''}
                          </div>
                          <div>PC {domicilioEnvio?.cp || ''}</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {/* {(orderData?.domicilioEnvio?.paqueteria === 'FEDEX' ||
                  orderData?.domicilioEnvio?.paqueteria === 'UPS') && (
                  <div className={styles.checkContainer}>
                    <Checkbox
                      disabled={orderData?.domicilioEnvio?.bloquearCambioDomicilio === 'true'}
                      checked={extraShippingharge}
                      style={{ padding: 0, paddingRight: 8 }}
                      onChange={(event) => postConfirmacionEnvio(event.target.checked)}
                    />
                    <span className={styles.colorGold}>{t('address:extraShippingCharge')}</span>
                  </div>
                )} */}
                <div className={styles.checkContainer}>
                  <Checkbox
                    disabled={orderData?.domicilioEnvio?.bloquearCambioDomicilio === 'true'}
                    checked={extraShippingharge}
                    style={{ padding: 0, paddingRight: 8 }}
                    onChange={(event) => postConfirmacionEnvio(event.target.checked)}
                  />
                  <span className={styles.colorGold}>{t('address:extraShippingCharge')}</span>
                </div>
              </>
            }>
            <div className={styles.selectSavedAddresses}>
              <SavedAddresses
                history={history}
                orderData={orderData}
                setOrderData={setOrderData}
                screenSize={screenSize}
                getCookie={getCookie}
                webservice={webservice}
                getOrder={getOrder}
                setOpenAddress={setOpenAddress}
                setLoaddingAddress={setLoaddingAddress}
                addressSelected={addressSelected}
                setAddressSelected={setAddressSelected}
                setSessionMessage={setSessionMessage}
                extraShippingharge={extraShippingharge}
                callback={() => {
                  setLoading(true);
                  getOrder('CERRAR01WEB', '', '', '', '', true);
                }}
              />
            </div>
          </OrderCard>

          <OrderCard>
            <div className={styles.orderDet}>
              <div className={styles.cardTextPaq}>
                <div className={styles.textIcon}>
                  <i className={`material-icons ${styles.icon}`}>local_shipping </i>
                  <label className={styles.iconText}>{t('shipping')}</label>
                </div>
              </div>
              <div className={styles.radioButtonsContainer}>
                {paqueterias.length > 0 &&
                  paqueterias.map((val, key) => {
                    return (
                      <div
                        className={styles.radioContainer}
                        key={key}
                        onClick={() => {
                          if (loading || finishLoading || val.paqueteria === paqueteria) return;
                          setPaqueteria(val.paqueteria);
                          updateParcel(val.paqueteria);
                        }}>
                        <i class={`material-icons-outlined ${styles.checkmark}`}>
                          {paqueteria === val.paqueteria
                            ? 'radio_button_checked'
                            : 'radio_button_unchecked'}
                        </i>
                        <span className={styles.radioText}>{val.paqueteria}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          </OrderCard>

          {/* <OrderCard>
            <div className={styles.orderDet}>
              <div className={styles.cardTextPaq}>
                <div className={styles.textIcon}>
                  <i className={`material-icons ${styles.icon}`}>local_shipping </i>
                  <label className={styles.iconText}>{t('shipping')}</label>
                </div>
              </div>
              {!loading && (
                <div className={styles.options}>
                  <select
                    className={styles.inputSelect}
                    onChange={(e) => changeValue(e)}
                    value={paqueteria}>
                    <option value=''>{t('shipping2')}</option>
                    {paqueterias.length > 0 &&
                      paqueterias.map((val, key) => {
                        return (
                          <option key={key} value={val.paqueteria}>
                            {val.paqueteria}
                          </option>
                        );
                      })}
                  </select>
                </div>
              )}
            </div>
          </OrderCard> */}

          <OrderCard title={t('addAnother')} open={addressForm} callback={setAddressForm}>
            <div className={styles.formContainer}>
              <AddressForm
                setAddressForm={setAddressForm}
                setSessionMessage={setSessionMessage}
                getCookie={getCookie}
                history={history}
                webservice={webservice}
                device={device}
                extraShippingharge={extraShippingharge}
                t={t}
                callback={() => {
                  setLoading(true);
                  getOrder('CERRAR01WEB', '', '', '', '', true);
                }}
              />
            </div>
          </OrderCard>

          <div className={styles.buttonContainer}>
            <ContinueButton />
          </div>
        </div>
      </div>
    </div>
  );
}
